import React, { useMemo, useState } from 'react';
import useDefaultSettings from '../Hooks/useDefaultSettings';
import Loading from '../../../Components/Loading';
import { useAppContext } from '../../../Services/AppContext';
import MagicForm from '../../../Components/Form/MagicForm';
import { ItemScheme } from '../../../Components/Form/MagicFormItem';
import { MagicField } from '../../../Enums/MagicField.enum';
import useMeta from '../../Meta/Hooks/useMeta';
import useRestApiClient from '../../Base/Hooks/useRestApiClient';

interface Props {
	onSave: () => void;
}

const formScheme: ItemScheme[] = [
	{
		type: MagicField.SELECT,
		key: 'sendMedicalReportDuringCaseClosing',
		title: 'component.defaultSettings.sendMedicalReportDuringCaseClosing',
		children: [
			{
				title: 'component.defaultSettings.yes',
				value: 'yes',
			},
			{
				title: 'component.defaultSettings.no',
				value: 'no',
			},
		],
		settings: {
			required: true,
			portal: true,
		},
	},
	{
		type: MagicField.SELECT,
		key: 'deliveryOfPrescriptionIdentifier',
		title: 'component.defaultSettings.deliveryOfPrescriptionIdentifier',
		children: [
			{
				title: 'component.defaultSettings.justMeddi',
				value: 'onlyToMEDDI',
			},
			{
				title: 'component.defaultSettings.meddiAndSMS',
				value: 'sms',
			},
			{
				title: 'component.defaultSettings.meddiAndEmail',
				value: 'email',
			},
		],
		settings: {
			required: true,
			portal: true,
		},
	},
	{
		type: MagicField.TEXT_FIELD,
		key: 'defaultPrescriptionValidityInDays',
		title: 'component.defaultSettings.defaultPrescriptionValidityInDays',
		subType: 'number',
		settings: {
			required: true,
			suffix: 'base.days.many',
		},
	},
];

const DefaultSettings: React.FC<Props> = ({ onSave }) => {
	const { userData } = useAppContext();
	const { showUnexpectedError } = useRestApiClient();
	const { createUserMetaBulk } = useMeta();
	const {
		sendMedicalReportDuringCaseClosing,
		deliveryOfPrescriptionIdentifier,
		defaultPrescriptionValidityInDays,
		isDefaultSettingsLoading,
		reloadDefaultSettings,
	} = useDefaultSettings();
	const [isLoading, setIsLoading] = useState(false);

	const defaultValues = {
		sendMedicalReportDuringCaseClosing,
		deliveryOfPrescriptionIdentifier,
		defaultPrescriptionValidityInDays: defaultPrescriptionValidityInDays ?? 14,
	};

	const handleSubmit = async (values: any) => {
		setIsLoading(true);

		const metaItems: { key: string; value: string | boolean | number }[] = [];

		Object.keys(values).map(item => {
			metaItems.push({
				key: item,
				value:
					typeof values[item] === 'string' || typeof values[item] === 'number'
						? values[item]
						: values[item].value,
			});
		});

		const result = await createUserMetaBulk({ userId: userData.id, metaItems });
		if (result?.status === 200) {
			await reloadDefaultSettings();
			onSave();
		} else {
			showUnexpectedError();
		}

		setIsLoading(false);
	};

	if (isLoading || isDefaultSettingsLoading) {
		return <Loading />;
	}

	return (
		<MagicForm
			formScheme={formScheme}
			defaultValues={defaultValues}
			onSubmit={handleSubmit}
		/>
	);
};

export default DefaultSettings;
